import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import './SodConflicts.css';
import API_URL from '../config';

const DEPARTMENTS = ['Finance', 'HR', 'IT', 'Operations', 'Security'];
const RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const SodConflicts = () => {
  const { token, user } = useAuth();
  const [rules, setRules] = useState([]);
  const [violations, setViolations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [scanResult, setScanResult] = useState('');
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    rule_name: '',
    group_a: 'Finance',
    group_b: 'HR',
    risk_level: 'HIGH',
    description: ''
  });

  const fetchRules = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/sod/rules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setRules(data);
    } catch (err) {
      console.error('Failed to fetch rules:', err);
    }
  }, [token]);

  const fetchViolations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/sod/violations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setViolations(data);
    } catch (err) {
      console.error('Failed to fetch violations:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchRules();
    fetchViolations();
  }, [fetchRules, fetchViolations]);

  const handleCreateRule = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    try {
      const res = await fetch(`${API_URL}/sod/rules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      setShowModal(false);
      setForm({ rule_name: '', group_a: 'Finance', group_b: 'HR', risk_level: 'HIGH', description: '' });
      fetchRules();
    } catch (err) {
      setError('Failed to create rule');
    }
  };

  const handleScan = async () => {
    setScanning(true);
    setScanResult('');
    setMessage('');
    setError('');

    try {
      const res = await fetch(`${API_URL}/sod/scan`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setScanResult(data.message);
      fetchViolations();
    } catch (err) {
      setError('Failed to run scan');
    } finally {
      setScanning(false);
    }
  };

  const handleRemediate = async (id) => {
    setMessage('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/sod/violations/${id}/remediate`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      fetchViolations();
    } catch (err) {
      setError('Failed to remediate violation');
    }
  };

  const handleException = async (id) => {
    setMessage('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/sod/violations/${id}/exception`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      fetchViolations();
    } catch (err) {
      setError('Failed to mark exception');
    }
  };

  const isAdmin = user?.role === 'ADMIN';
  const openViolations = violations.filter(v => v.status === 'OPEN').length;

  return (
    <Layout>
      <div className="sod-header">
        <h1>SoD Conflicts</h1>
        <div className="sod-actions">
          {isAdmin && (
            <button className="btn-primary" onClick={() => setShowModal(true)}>
              + Add Rule
            </button>
          )}
          <button
            className="btn-scan"
            onClick={handleScan}
            disabled={scanning}
          >
            {scanning ? 'Scanning...' : '▶ Run Scan'}
          </button>
        </div>
      </div>

      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}
      {scanResult && <div className="scan-result">🔍 {scanResult}</div>}

      <div className="sod-grid">
        {/* Rules */}
        <div className="sod-section">
          <div className="sod-section-header">
            <h2>SoD Rules</h2>
            <span style={{ fontSize: '13px', color: '#666' }}>{rules.length} rules</span>
          </div>
          {rules.length === 0 ? (
            <div className="empty-state">No SoD rules defined yet.</div>
          ) : (
            <table className="sod-table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Group A</th>
                  <th>Group B</th>
                  <th>Risk</th>
                </tr>
              </thead>
              <tbody>
                {rules.map(rule => (
                  <tr key={rule.id}>
                    <td>{rule.rule_name}</td>
                    <td>{rule.group_a}</td>
                    <td>{rule.group_b}</td>
                    <td>
                      <span className={`risk-badge risk-${rule.risk_level}`}>
                        {rule.risk_level}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Violations Summary */}
        <div className="sod-section">
          <div className="sod-section-header">
            <h2>Violation Summary</h2>
            <span style={{ fontSize: '13px', color: '#666' }}>{openViolations} open</span>
          </div>
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              {['OPEN', 'REMEDIATED', 'EXCEPTION'].map(status => (
                <div key={status} style={{
                  textAlign: 'center', padding: '16px',
                  background: '#f8f9fa', borderRadius: '8px'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#003366' }}>
                    {violations.filter(v => v.status === status).length}
                  </div>
                  <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase',
                               letterSpacing: '0.5px', marginTop: '4px' }}>
                    {status}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Violations Table */}
      <div className="sod-section">
        <div className="sod-section-header">
          <h2>Violations</h2>
          <span style={{ fontSize: '13px', color: '#666' }}>{violations.length} total</span>
        </div>
        {violations.length === 0 ? (
          <div className="empty-state">No violations detected. Run a scan to check for conflicts.</div>
        ) : (
          <table className="sod-table">
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Email</th>
                <th>Rule</th>
                <th>Groups</th>
                <th>Risk</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {violations.map((v, index) => (
                <tr key={v.id}>
                  <td>{index + 1}</td>
                  <td>{v.user_name}</td>
                  <td>{v.user_email}</td>
                  <td>{v.rule_name}</td>
                  <td>{v.group_a} + {v.group_b}</td>
                  <td>
                    <span className={`risk-badge risk-${v.risk_level}`}>
                      {v.risk_level}
                    </span>
                  </td>
                  <td>
                    <span className={`violation-status status-${v.status}`}>
                      {v.status}
                    </span>
                  </td>
                  <td>
                    {v.status === 'OPEN' && isAdmin ? (
                      <div className="violation-actions">
                        <button className="btn-remediate" onClick={() => handleRemediate(v.id)}>
                          Remediate
                        </button>
                        <button className="btn-exception" onClick={() => handleException(v.id)}>
                          Exception
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        {v.remediated_by_name || '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add SoD Rule</h2>
            <form onSubmit={handleCreateRule}>
              <div className="form-group">
                <label>Rule Name</label>
                <input
                  type="text"
                  value={form.rule_name}
                  onChange={e => setForm({ ...form, rule_name: e.target.value })}
                  placeholder="e.g. Finance-Security Conflict"
                  required
                />
              </div>
              <div className="form-group">
                <label>Group A</label>
                <select value={form.group_a} onChange={e => setForm({ ...form, group_a: e.target.value })}>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Group B</label>
                <select value={form.group_b} onChange={e => setForm({ ...form, group_b: e.target.value })}>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Risk Level</label>
                <select value={form.risk_level} onChange={e => setForm({ ...form, risk_level: e.target.value })}>
                  {RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Explain why this combination is a conflict..."
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Create Rule</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SodConflicts;